import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { IServicePackage } from '@clickersphere/core/entities/IServicePackage';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { useCreateServicePackage } from '../../../../app/hooks/service-package/useCreateServicePackage';
import { useUpdateServicePackage } from '../../../../app/hooks/service-package/useUpdateServicePackage';
import { useServices } from '../../../../app/hooks/service/useServices';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';

interface IUseRegisterServicePackageFormController {
  servicePackage?: IServicePackage;
}

type FormData = {
  name: string;
  description?: string;
  price: number;
  imageUrl?: string | null;
  serviceIds: string[];
};

export function useRegisterServicePackageFormController({
  servicePackage,
}: IUseRegisterServicePackageFormController) {
  const { t } = useTranslation();
  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );
  const { toast } = useToast();
  const { handleError } = useError();
  const { createServicePackageAsync, createServicePackageIsPending } =
    useCreateServicePackage();
  const { updateServicePackageAsync, updateServicePackageIsPending } =
    useUpdateServicePackage();
  const { services } = useServices();

  const priceManuallyEdited = useRef(false);

  const schema = z.object({
    name: z
      .string()
      .min(1, t('catalog.form.errors.nameRequired'))
      .max(100, t('catalog.form.errors.nameMax')),
    description: z
      .string()
      .max(500, t('catalog.form.errors.descriptionMax'))
      .optional()
      .transform((v) => (v === '' ? undefined : v)),
    price: z
      .number({ message: t('catalog.form.errors.pricePositive') })
      .positive(t('catalog.form.errors.pricePositive')),
    imageUrl: z.union([z.string(), z.null()]).optional(),
    serviceIds: z
      .array(z.string().uuid())
      .min(1, t('catalog.form.errors.servicesMin')),
  });

  const {
    handleSubmit: hookFormHandleSubmit,
    register,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: servicePackage
      ? {
          name: servicePackage.name,
          description: servicePackage.description ?? '',
          price: servicePackage.price / 100,
          imageUrl: undefined,
          serviceIds: servicePackage.services?.map((s) => s.id) ?? [],
        }
      : {
          name: '',
          description: '',
          price: 0,
          imageUrl: undefined,
          serviceIds: [],
        },
  });

  const watchedServiceIds = watch('serviceIds');

  useEffect(() => {
    if (priceManuallyEdited.current) return;
    if (!services || watchedServiceIds.length === 0) return;

    const total = watchedServiceIds.reduce((acc, id) => {
      const svc = services.find((s) => s.id === id);
      return acc + (svc ? svc.price / 100 : 0);
    }, 0);

    if (getValues('price') !== total) {
      setValue('price', total);
    }
  }, [watchedServiceIds, services, setValue, getValues]);

  const handlePriceChange = () => {
    priceManuallyEdited.current = true;
  };

  const serviceOptions = (services ?? []).map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    const imageUrlPayload =
      data.imageUrl === null
        ? null
        : data.imageUrl && data.imageUrl !== ''
          ? data.imageUrl
          : undefined;

    try {
      if (!servicePackage) {
        const createPayload = {
          name: data.name,
          description: data.description,
          price: Math.round(data.price * 100),
          serviceIds: data.serviceIds,
          ...(typeof imageUrlPayload === 'string'
            ? { imageUrl: imageUrlPayload }
            : {}),
        };
        await createServicePackageAsync(createPayload);
      } else {
        const updatePayload: {
          name: string;
          description?: string;
          price: number;
          imageUrl?: string | null;
          serviceIds: string[];
        } = {
          name: data.name,
          description: data.description,
          price: Math.round(data.price * 100),
          serviceIds: data.serviceIds,
        };
        if (imageUrlPayload !== undefined) {
          updatePayload.imageUrl = imageUrlPayload;
        }
        await updateServicePackageAsync({
          id: servicePackage.id,
          data: updatePayload,
        });
      }
      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
      } else {
        toast(t('catalog.form.actions.saveError'), 'error');
      }
    }
  });

  const handleFormReset = () => {
    priceManuallyEdited.current = false;
    reset();
  };

  return {
    isFetching: createServicePackageIsPending || updateServicePackageIsPending,
    errors,
    control,
    register,
    handleSubmit,
    handleFormReset,
    serviceOptions,
    handlePriceChange,
  };
}
